import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button, Header, Modal } from "@pentabd/ui";

import { yupResolver } from "@hookform/resolvers/yup";

import {
  ErrorModal,
  InputDateControllerVertical,
  InputTextVertical,
  MultiFile,
  SuccessModal,
} from "@components";

import { addInstallment } from "@api/gpf";

import { ROUTES } from "@constants/routes";

import {
  AddNewInstallmentFormDataType,
  addNewInstallmentValidation,
  CREATE_NEW_INSTALLMENT,
} from "@validations/HRM/GPF/addInstallmentValidation";
import { convertMultipleDocumentData } from "@utils/documentDataConvert";

const {
  HRM: { PROVIDENT_FUND },
} = ROUTES;
interface InstallmentModalType {
  loanID: string;
  employeeID: string;
  isModalOpen: boolean;
  closeModal: () => void;
}

export default function AddInstallmentModal({
  loanID,
  employeeID,
  isModalOpen,
  closeModal,
}: InstallmentModalType) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const methods = useForm<AddNewInstallmentFormDataType>({
    resolver: yupResolver(addNewInstallmentValidation),
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    control,
    reset,
  } = methods;
  const onSubmit: SubmitHandler<AddNewInstallmentFormDataType> = (
    data: any,
  ) => {
    addInstallment(
      employeeID,
      loanID,
      convertMultipleDocumentData(data, [`${CREATE_NEW_INSTALLMENT.DOCUMENT}`]),
      (response) => {
        closeModal();
        if (response.status === "success") {
          setIsSuccessModalOpen(true);
          reset();
        } else {
          setIsErrorModalOpen(true);
        }
      },
    );
  };

  const closeSuccessModal = () => setIsSuccessModalOpen(false);
  const closeErrorModal = () => setIsErrorModalOpen(false);

  const handleOkButton = () => {
    closeSuccessModal();
    closeModal();
    navigate(PROVIDENT_FUND);
  };

  return (
    <div>
      {/* AFTER EFFECTS MODAL */}
      <SuccessModal
        isModalOpen={isSuccessModalOpen}
        subtitle="Installment added successfully"
        action={handleOkButton}
      />
      <ErrorModal isModalOpen={isErrorModalOpen} closeModal={closeErrorModal} />
      {/* AFTER EFFECTS MODAL */}
      <Modal
        isOpen={isModalOpen}
        overlay={isModalOpen}
        onClose={closeModal}
        portal
        closeAble
      >
        <Header
          size="sm"
          headerText={{
            header: t("GPF.MODAL.HEADER"),
            subHeader: `Loan#${loanID}`,
          }}
          className="px-9"
        />
        <div className="gpf-installment-modal px-9 py-10">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="d-flex flex-column gap-8">
              <InputTextVertical
                label="GPF.MODAL.FORM.AMOUNT"
                register={{
                  ...register(CREATE_NEW_INSTALLMENT.INSTALLMENT_AMOUNT),
                }}
                error={errors}
                name={CREATE_NEW_INSTALLMENT.INSTALLMENT_AMOUNT}
                required
              />

              <InputTextVertical
                label="GPF.MODAL.FORM.FILE_NAME"
                register={{
                  ...register(CREATE_NEW_INSTALLMENT.INSTALLMENT_FILE_NAME),
                }}
                error={errors}
                name={CREATE_NEW_INSTALLMENT.INSTALLMENT_FILE_NAME}
                required
              />

              <InputDateControllerVertical
                control={control}
                label="GPF.MODAL.FORM.DATE"
                name={CREATE_NEW_INSTALLMENT.INSTALLMENT_DATE}
                register={{
                  ...register(CREATE_NEW_INSTALLMENT.INSTALLMENT_DATE),
                }}
                errors={errors}
                required
              />

              <MultiFile
                id={CREATE_NEW_INSTALLMENT.DOCUMENT}
                control={control}
                setValue={setValue}
                register={register}
                errors={errors}
                fileDetails={["documentName", "date", "remarks", "memorialNo"]}
              />
            </div>
            <div className="py-9 d-flex justify-content-end align-items-center gap-6">
              <Button
                size="sm"
                fill="outline"
                type="light"
                onClick={closeModal}
              >
                {t("GPF.MODAL.CANCEL_BUTTON")}
              </Button>
              <Button size="sm" htmlType="submit" fill="fill" type="primary">
                {t("GPF.MODAL.SUBMIT_BUTTON")}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}
